import { useMemo, useState } from "react";
import NewsCard from "../../Components/NewsCard/NewsCard";
import { useConfigStore } from "../../Stores/Config";
import { useNewsStore } from "../../Stores/News";
import { ReactComponent as AnnouncementsActive } from "../../Assets/megaphone-solid.svg";
import { activateOnKey } from "../../Helper/a11y";
import "./News.scss";

type NewsFilter = "all" | "news" | "changelog";

function News() {
  const newsStore = useNewsStore();
  const articles = useNewsStore((state) => state.articles);
  const configStore = useConfigStore();
  const [filter, setFilter] = useState<NewsFilter>("all");

  const hasMultipleTypes = useMemo(() => {
    const hasNews = articles.some((a) => a.subType !== "CHANGELOG");
    const hasChangelog = articles.some((a) => a.subType === "CHANGELOG");
    return hasNews && hasChangelog;
  }, [articles]);

  const filteredArticles = useMemo(() => {
    if (filter === "all") return articles;
    if (filter === "changelog") return articles.filter((a) => a.subType === "CHANGELOG");
    return articles.filter((a) => a.subType !== "CHANGELOG");
  }, [articles, filter]);

  return (
    <div className="news">
      <div className="news-content">
        {hasMultipleTypes && (
          <div className="news-filter-bar">
            <div
              className={`news-filter-bar-item ${filter === "all" ? "active" : ""}`}
              role="button"
              tabIndex={0}
              aria-pressed={filter === "all"}
              onClick={() => setFilter("all")}
              onKeyDown={activateOnKey(() => setFilter("all"))}
            >
              {configStore.translateText("all")}
            </div>
            <div
              className={`news-filter-bar-item ${filter === "news" ? "active" : ""}`}
              role="button"
              tabIndex={0}
              aria-pressed={filter === "news"}
              onClick={() => setFilter("news")}
              onKeyDown={activateOnKey(() => setFilter("news"))}
            >
              {configStore.translateText("news")}
            </div>
            <div
              className={`news-filter-bar-item ${filter === "changelog" ? "active" : ""}`}
              role="button"
              tabIndex={0}
              aria-pressed={filter === "changelog"}
              onClick={() => setFilter("changelog")}
              onKeyDown={activateOnKey(() => setFilter("changelog"))}
            >
              {configStore.translateText("changelogs")}
            </div>
          </div>
        )}
        {filteredArticles.length === 0 && !newsStore.loadingNews && (
          <div className="no-news">
            <AnnouncementsActive className="no-news-yet" />
            <span className="title" role="heading" aria-level={1}>{configStore.config.newsMenuLabel}</span>
            <span className="description">
              {configStore.translateText("noNews")}
            </span>
          </div>
        )}
        {filteredArticles.map((news) => {
          return <NewsCard news={news} key={news.id} />;
        })}
        {newsStore.articles.length !== 0 && newsStore.hasMore && (
          <div className="load-more-container">
            <div
              className={`load-more`}
              role="button"
              tabIndex={0}
              onClick={() => {
                newsStore.loadNews();
              }}
              onKeyDown={activateOnKey(() => {
                newsStore.loadNews();
              })}
            >
              <span>{configStore.translateText("loadMore")}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default News;
